import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { AnimatePresence } from 'framer-motion';
import { motion } from 'framer-motion';
import { memo, useState } from 'react';
import { Button } from '~/components/ui/Button';
import { IconButton } from '~/components/ui/IconButton';
import {
  AGENT_OPTIONS,
  getAgentColorVariable,
  getAgentDescription,
  getAgentIcon,
  getAgentName,
  getAgentProperties,
} from '~/lib/agents';
import type { SupportedAgents } from '~/lib/agents';
import { classNames } from '~/utils/classNames';

interface PromptAgentSelectProps {
  agent: SupportedAgents;
  onChange: (agent: SupportedAgents) => Promise<void>;
  requireConfirmation?: boolean;
  mode?: 'submenu' | 'dropdown';
}

export const PromptAgentSelect = memo((props: PromptAgentSelectProps) => {
  const { agent, onChange } = props;
  const [open, setOpen] = useState(false);
  const [nextAgent, setNextAgent] = useState<SupportedAgents | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedAgent, setHighlightedAgent] = useState<SupportedAgents | null>(null);

  if (props.mode === 'dropdown') {
    return (
      <DropdownMenu.Root
        modal={false}
        open={open}
        onOpenChange={() => {
          if (nextAgent) {
            setNextAgent(null);
          } else {
            setOpen(!open);
            setHighlightedAgent(null);
          }
        }}
      >
        <DropdownMenu.Trigger asChild>
          <Button
            variant="ghost"
            size="sm"
            startIcon={classNames(
              getAgentIcon(agent),
              getAgentColorVariable(agent),
              'size-4',
              'group-hover/button:text-[--agent-color]',
            )}
            endIcon="i-heroicons:chevron-up-down size-3 opacity-70 group-hover/button:opacity-100"
            className="rounded-full group/button font-normal text-bolt-elements-textTertiary dark:text-bolt-elements-textTertiary hover:text-bolt-elements-textPrimary"
          >
            <span className="ml-1 text-sm">{getAgentName(agent, { removeLegacy: true })}</span>
          </Button>
        </DropdownMenu.Trigger>

        <AnimatePresence>
          {open && (
            <DropdownMenu.Portal forceMount>
              <DropdownMenu.Content
                asChild
                sideOffset={5}
                align="start"
                side="top"
                onPointerDownOutside={() => {
                  setOpen(false);
                }}
              >
                <motion.div
                  className="origin-[0_100%] z-10"
                  initial={{ opacity: 0, y: 0, filter: 'blur(2px)' }}
                  animate={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, y: 0, scale: 0.9, filter: 'blur(2px)' }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                >
                  <AnimatePresence>
                    {highlightedAgent && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15, ease: 'easeOut' }}
                        className="absolute left-full translate-x-2 w-[280px] bottom-0"
                      >
                        <div className="text-xs grid gap-px rounded-md p-0.5 shadow-sm bg-bolt-elements-background-depth-1 dark:bg-bolt-elements-background-depth-2 border-bolt-elements-borderColor border p-4">
                          <div className="font-semibold">{getAgentName(highlightedAgent)}</div>

                          <div className="mt-2 text-xs text-bolt-elements-textPrimary">
                            {getAgentDescription(highlightedAgent)}
                          </div>

                          <div className="mt-2 grid gap-1">
                            {getAgentProperties(highlightedAgent).map((item) => (
                              <div className="flex justify-between items-center" key={item.label}>
                                <div className="text-bolt-elements-textSecondary">{item.label}</div>
                                <div className="flex gap-1">
                                  {Array.from({ length: 5 })
                                    .fill(0)
                                    .fill(1, 0, item.value)
                                    .map((value, index) => (
                                      <div
                                        key={index}
                                        className={classNames(
                                          'w-4 h-1 rounded-full',
                                          value
                                            ? 'bg-bolt-elements-button-primary-background'
                                            : 'bg-bolt-elements-button-secondary-background',
                                        )}
                                      />
                                    ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <motion.div
                    layout
                    className="grid gap-px transition-width duration-1000 rounded-md p-0.5 shadow-sm bg-bolt-elements-background-depth-1 dark:bg-bolt-elements-background-depth-2 border-bolt-elements-borderColor border"
                  >
                    <AnimatePresence mode="wait">
                      {!nextAgent && (
                        <motion.div
                          onMouseLeave={() => {
                            setHighlightedAgent(null);
                          }}
                          className="min-w-[192px]"
                          key="agent-select"
                          initial={{ opacity: 0, filter: 'blur(2px)' }}
                          animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                          exit={{ opacity: 0, scale: 0.9, filter: 'blur(2px)' }}
                          transition={{ duration: 0.15, ease: 'easeOut' }}
                        >
                          <div className="px-2 py-1 text-xs text-bolt-elements-text-secondary opacity-60">
                            Select assistant
                          </div>

                          {AGENT_OPTIONS.map((item) => (
                            <div className="group/item" key={item.label}>
                              <DropdownMenu.Item
                                disabled={item.disabled}
                                aria-current={item.value === agent}
                                autoFocus={item.value === agent}
                                className={`
                                  relative flex h-8 select-none items-center pl-8.5 pr-2 text-sm flex rounded outline-none
                                  data-[disabled]:pointer-events-none
                                  data-[disabled]:cursor-not-allowed
                                  data-[disabled]:opacity-40
                                  data-[highlighted]:bg-bolt-elements-item-backgroundActive
                                  data-[highlighted]:text-bolt-elements-item-contentActive
                                  group/item-child
                                `}
                                onKeyUp={() => {
                                  setHighlightedAgent(item.value);
                                }}
                                onPointerMove={() => {
                                  setHighlightedAgent(item.value);
                                }}
                                onSelect={(event) => {
                                  if (item.value === agent) {
                                    return;
                                  }

                                  if (props.requireConfirmation) {
                                    event.preventDefault();
                                    setNextAgent(item.value);
                                    setHighlightedAgent(null);
                                  } else {
                                    onChange(item.value);
                                  }
                                }}
                              >
                                <div className="absolute left-2.5 inline-flex size-4 items-center justify-center">
                                  <span
                                    className={classNames(item.icon, item.colorVariable, 'size-4 text-[--agent-color]')}
                                  />
                                </div>

                                <span>{item.label}</span>

                                {item.disabled && (
                                  <div className="ml-2 bg-bolt-ds-surfaceThree text-bolt-ds-textPrimary px-2 py-0.5 rounded-full text-[9px] uppercase font-medium tracking-wider opacity-0 group-hover/item:opacity-100 transition-theme">
                                    Coming soon
                                  </div>
                                )}

                                {item.value === agent && (
                                  <div className="ml-2 bg-bolt-ds-brandContainer text-bolt-ds-brandHighlight px-2 py-0.5 rounded-full text-[9px] uppercase font-medium tracking-wider">
                                    Active
                                  </div>
                                )}
                              </DropdownMenu.Item>
                            </div>
                          ))}
                        </motion.div>
                      )}

                      {nextAgent && (
                        <motion.div
                          layout
                          key="agent-select-confirm"
                          initial={{ width: 192, height: 120 }}
                          animate={{ width: 'auto', height: 'auto' }}
                          exit={{ width: 192, height: 120, transition: { delay: 0.15 } }}
                          transition={{ duration: 0.15, ease: 'easeOut' }}
                          className="overflow-hidden"
                        >
                          <motion.div
                            initial={{ opacity: 0, filter: 'blur(2px)' }}
                            animate={{ opacity: 1, scale: 1, filter: 'blur(0px)', transition: { delay: 0.15 } }}
                            exit={{ opacity: 0, scale: 0.9, filter: 'blur(2px)' }}
                            transition={{ duration: 0.15, ease: 'easeOut' }}
                            className="text-sm p-4 text-bolt-ds-textSecondary"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="font-semibold text-nowrap text-bolt-ds-textPrimary">
                                Confirm agent change?
                              </div>

                              <IconButton
                                icon="i-heroicons:x-mark"
                                variant="ghost"
                                className="absolute top-1 right-1"
                                onClick={() => setNextAgent(null)}
                                size="sm"
                              />
                            </div>

                            <p className="text-nowrap mt-2">
                              Switching agent will{' '}
                              <b className="text-bolt-ds-textPrimary font-semibold">clear your chat history</b>.
                            </p>

                            <div className="flex gap-2 mt-4 justify-end">
                              <DropdownMenu.Item
                                asChild
                                onSelect={async () => {
                                  setIsLoading(true);
                                  await onChange(nextAgent);
                                  setIsLoading(false);
                                  setNextAgent(null);
                                  setOpen(false);
                                }}
                              >
                                <Button
                                  variant="high-contrast"
                                  loading={isLoading}
                                  size="sm"
                                  startIcon={classNames(
                                    getAgentIcon(nextAgent),
                                    getAgentColorVariable(nextAgent),
                                    'size-4 text-[--agent-color]',
                                  )}
                                  autoFocus
                                >
                                  <span className="pl-1">Switch to {getAgentName(nextAgent)}</span>
                                </Button>
                              </DropdownMenu.Item>
                            </div>
                          </motion.div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                </motion.div>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          )}
        </AnimatePresence>
      </DropdownMenu.Root>
    );
  }

  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger
        className={`
          group group/button relative flex h-8 select-none items-center pl-8.5 pr-6 text-sm flex rounded outline-none
          data-[state=open]:bg-bolt-elements-item-backgroundActive
          data-[state=open]:text-bolt-elements-item-contentActive
          data-[highlighted]:bg-bolt-elements-item-backgroundActive
          data-[highlighted]:text-bolt-elements-item-contentActive
        `}
      >
        <div className="absolute left-2.5 inline-flex size-4 items-center justify-center">
          <span
            className={classNames(
              getAgentIcon(agent),
              getAgentColorVariable(agent),
              'size-4',
              'group-data-[state=open]/button:text-[--agent-color] text-bolt-elements-textTertiary',
            )}
          />
        </div>
        <span>{getAgentName(agent, { removeLegacy: true })}</span>
        <span className="i-heroicons:chevron-right size-3 opacity-70 ml-auto" />
      </DropdownMenu.SubTrigger>

      <DropdownMenu.Portal>
        <DropdownMenu.SubContent asChild alignOffset={-91} sideOffset={5}>
          <motion.div
            className="origin-[0_100%] z-10"
            initial={{ opacity: 0, y: 0, filter: 'blur(2px)' }}
            animate={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
            exit={{ opacity: 0, y: 0, scale: 0.9, filter: 'blur(2px)' }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
          >
            <AnimatePresence>
              {highlightedAgent && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                  className="absolute left-full translate-x-2 w-[280px] bottom-0"
                >
                  <div className="text-xs grid gap-px rounded-md p-0.5 shadow-sm bg-bolt-elements-background-depth-1 dark:bg-bolt-elements-background-depth-2 border-bolt-elements-borderColor border p-4">
                    <div className="font-semibold">{getAgentName(highlightedAgent)}</div>

                    <div className="mt-2 text-xs text-bolt-elements-textPrimary">
                      {getAgentDescription(highlightedAgent)}
                    </div>

                    <div className="mt-2 grid gap-1">
                      {getAgentProperties(highlightedAgent).map((item) => (
                        <div className="flex justify-between items-center" key={item.label}>
                          <div className="text-bolt-elements-textSecondary">{item.label}</div>
                          <div className="flex gap-1">
                            {Array.from({ length: 5 })
                              .fill(0)
                              .fill(1, 0, item.value)
                              .map((value, index) => (
                                <div
                                  key={index}
                                  className={classNames(
                                    'w-4 h-1 rounded-full',
                                    value
                                      ? 'bg-bolt-elements-button-primary-background'
                                      : 'bg-bolt-elements-button-secondary-background',
                                  )}
                                />
                              ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <motion.div
              layout
              className="grid gap-px transition-width duration-1000 rounded-md p-0.5 shadow-sm bg-bolt-elements-background-depth-1 dark:bg-bolt-elements-background-depth-2 border-bolt-elements-borderColor border"
            >
              <AnimatePresence mode="wait">
                {!nextAgent && (
                  <motion.div
                    onMouseLeave={() => {
                      setHighlightedAgent(null);
                    }}
                    className="min-w-[192px]"
                    key="agent-select"
                    initial={{ opacity: 0, filter: 'blur(2px)' }}
                    animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, scale: 0.9, filter: 'blur(2px)' }}
                    transition={{ duration: 0.15, ease: 'easeOut' }}
                  >
                    <div className="px-2 py-1 text-xs text-bolt-elements-text-secondary opacity-60">
                      Select assistant
                    </div>

                    {AGENT_OPTIONS.map((item) => (
                      <div className="group/item" key={item.label}>
                        <DropdownMenu.Item
                          disabled={item.disabled}
                          aria-current={item.value === agent}
                          autoFocus={item.value === agent}
                          className={`
                            relative flex h-8 select-none items-center pl-8.5 pr-2 text-sm flex rounded outline-none
                            data-[disabled]:pointer-events-none
                            data-[disabled]:cursor-not-allowed
                            data-[disabled]:opacity-40
                            data-[highlighted]:bg-bolt-elements-item-backgroundActive
                            data-[highlighted]:text-bolt-elements-item-contentActive
                            group/item-child
                          `}
                          onKeyUp={() => {
                            setHighlightedAgent(item.value);
                          }}
                          onPointerMove={() => {
                            setHighlightedAgent(item.value);
                          }}
                          onSelect={(event) => {
                            if (item.value === agent) {
                              return;
                            }

                            if (props.requireConfirmation) {
                              event.preventDefault();
                              setNextAgent(item.value);
                              setHighlightedAgent(null);
                            } else {
                              onChange(item.value);
                            }
                          }}
                        >
                          <div className="absolute left-2.5 inline-flex size-4 items-center justify-center">
                            <span
                              className={classNames(item.icon, item.colorVariable, 'size-4 text-[--agent-color]')}
                            />
                          </div>

                          <span>{item.label}</span>

                          {item.disabled && (
                            <div className="ml-2 bg-bolt-ds-surfaceThree text-bolt-ds-textPrimary px-2 py-0.5 rounded-full text-[9px] uppercase font-medium tracking-wider opacity-0 group-hover/item:opacity-100 transition-theme">
                              Coming soon
                            </div>
                          )}

                          {item.value === agent && (
                            <div className="ml-2 bg-bolt-ds-brandContainer text-bolt-ds-brandHighlight px-2 py-0.5 rounded-full text-[9px] uppercase font-medium tracking-wider">
                              Active
                            </div>
                          )}
                        </DropdownMenu.Item>
                      </div>
                    ))}
                  </motion.div>
                )}

                {nextAgent && (
                  <motion.div
                    layout
                    key="agent-select-confirm"
                    initial={{ width: 192, height: 120 }}
                    animate={{ width: 'auto', height: 'auto' }}
                    exit={{ width: 192, height: 120, transition: { delay: 0.15 } }}
                    transition={{ duration: 0.15, ease: 'easeOut' }}
                    className="overflow-hidden"
                  >
                    <motion.div
                      initial={{ opacity: 0, filter: 'blur(2px)' }}
                      animate={{ opacity: 1, scale: 1, filter: 'blur(0px)', transition: { delay: 0.15 } }}
                      exit={{ opacity: 0, scale: 0.9, filter: 'blur(2px)' }}
                      transition={{ duration: 0.15, ease: 'easeOut' }}
                      className="text-sm p-4 text-bolt-ds-textSecondary"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-semibold text-nowrap text-bolt-ds-textPrimary">Confirm agent change?</div>

                        <IconButton
                          icon="i-heroicons:x-mark"
                          variant="ghost"
                          className="absolute top-1 right-1"
                          onClick={() => setNextAgent(null)}
                          size="sm"
                        />
                      </div>

                      <p className="text-nowrap mt-2">
                        Switching agent will{' '}
                        <b className="text-bolt-ds-textPrimary font-semibold">clear your chat history</b>.
                      </p>

                      <div className="flex gap-2 mt-4 justify-end">
                        <DropdownMenu.Item
                          asChild
                          onSelect={async () => {
                            setIsLoading(true);
                            await onChange(nextAgent);
                            setIsLoading(false);
                            setNextAgent(null);
                          }}
                        >
                          <Button
                            variant="high-contrast"
                            loading={isLoading}
                            size="sm"
                            startIcon={classNames(
                              getAgentIcon(nextAgent),
                              getAgentColorVariable(nextAgent),
                              'size-4 text-[--agent-color]',
                            )}
                            autoFocus
                          >
                            <span className="pl-1">Switch to {getAgentName(nextAgent)}</span>
                          </Button>
                        </DropdownMenu.Item>
                      </div>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        </DropdownMenu.SubContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Sub>
  );
});
